import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Criteria } from "@/types/criteria";
import { Info, ClipboardList } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { loadGlobalCriteria } from "@/lib/criteriaLoader";

interface CriteriaInfoCardProps {
  className?: string;
}

export function CriteriaInfoCard({ className }: CriteriaInfoCardProps) {
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadCriteria();
    
    const handleStorageChange = () => loadCriteria();
    window.addEventListener("storage-sync", handleStorageChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage-sync", handleStorageChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const loadCriteria = async () => {
    const activeCriteria = await loadGlobalCriteria();
    setCriteria(activeCriteria);
  };

  const activeCriteria = criteria.filter(c => c.active).sort((a, b) => a.order - b.order);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Bewertungskriterien
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Info className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Erklärung der Bewertungskriterien
                </DialogTitle>
              </DialogHeader>
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-4 pb-4">
                  {activeCriteria.map((criterion) => (
                    <div key={criterion.id} className="space-y-1">
                      <div className="flex items-center gap-2">
                        {criterion.icon && <criterion.icon className="h-4 w-4 text-primary" />}
                        <span className="font-medium">{criterion.label}</span>
                        <Badge variant="secondary" className="text-xs">
                          {criterion.defaultPoints} Pkt.
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground pl-6">
                        {criterion.description || "Keine Beschreibung verfügbar."}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground mb-3">
          Folgende Kriterien werden bei Ihrer Leistungsbewertung berücksichtigt:
        </p>
        <div className="space-y-2">
          <TooltipProvider>
            {activeCriteria.map((criterion) => (
              <Tooltip key={criterion.id} delayDuration={300}>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-help">
                    <div className="flex items-center gap-2">
                      {criterion.icon && <criterion.icon className="h-4 w-4 text-primary" />}
                      <span className="text-sm">{criterion.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {criterion.defaultPoints} Pkt.
                      </Badge>
                      <Info className="h-3 w-3 text-muted-foreground" />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-sm">
                    {criterion.description || "Keine Beschreibung verfügbar."}
                  </p>
                </TooltipContent>
              </Tooltip>
            ))}
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}

export default CriteriaInfoCard;
